// src/utils/estimateConflict.js — doced-8f3a61c2 (Document editor, Stage 3: groundwork, no screen)
//
// "Someone else changed this estimate." The new page remembers what the estimate looked like when it was
// opened (a fingerprint), and SAVES INSIDE A TRANSACTION that re-reads the estimate and refuses to write
// if it no longer matches. So a save never silently overwrites another person's (or the customer's) change:
// the page shows what changed and lets the person reload, or overwrite on purpose.
//
// WHAT COUNTS AS A CHANGE: only the fields this editor shows and saves, plus the status and the deposit's
// state — e.g. another staff member's edit in the pop-up, the phone or another tab; the customer approving
// or declining in the portal; a deposit being paid. NOT counted: things the server or the scheduler change
// on their own (number assignment, pipeline stage, follow-up reminder bookkeeping, view tracking, activity
// stamps, updatedAt) — the save merges, so those are kept anyway, and counting them would warn about
// nothing. After its OWN save (or whenever a change arrives while there are no unsaved edits) the page takes
// a fresh fingerprint (fingerprintOf on the live document).
//
// Pure except saveWithConflictCheck, which takes Firestore's runTransaction as an argument.

export const CONFLICT_FIELDS = Object.freeze([
    'customerId', 'customerName', 'customerDetails', 'propertyId', 'propertyName', 'serviceAddress', 'billTo',
    'title', 'description', 'notes', 'docType', 'status', 'createdAt', 'expiresAt', 'estimatedDuration',
    'salesPersonId', 'lineItems', 'subtotal', 'taxAmount', 'total',
    'deposit', 'depositRequired', 'depositStatus',
    'terms', // doced-8f3a61c2 — Stage 5e: the customer-facing Terms the page now edits
    'depositAmountNeeded', // depdraft-3c8e5a14 — "Collect a deposit" ON with no amount (absent = false)
]);

// Firestore Timestamps and Dates → milliseconds; objects with sorted keys; undefined = absent.
function normal(v) {
    if (v === undefined) return undefined;
    if (v === null || typeof v !== 'object') return v;
    if (typeof v.toMillis === 'function') return { $ms: v.toMillis() };
    if (v instanceof Date) return { $ms: v.getTime() };
    if (Array.isArray(v)) return v.map((x) => (x === undefined ? null : normal(x)));
    const out = {};
    for (const k of Object.keys(v).sort()) {
        const n = normal(v[k]);
        if (n !== undefined) out[k] = n;
    }
    return out;
}

// Changes that can NEVER be overwritten from the page, only reloaded: the status (e.g. the customer approved
// or declined in the portal) and the deposit (paid, recorded, waived, refunded). Overwriting them would put a
// stale status or deposit back over what really happened.
export const RELOAD_ONLY_FIELDS = Object.freeze(['status', 'deposit', 'depositRequired', 'depositStatus', 'depositAmountNeeded']); // depdraft-3c8e5a14 — part of the deposit

/** The fingerprint of an estimate's stored data: one string per field, for the fields above. */
export function fingerprintOf(data) {
    const d = data || {};
    const fp = {};
    for (const f of CONFLICT_FIELDS) fp[f] = JSON.stringify(normal(d[f]) ?? null);
    fp.depositAmountNeeded = JSON.stringify(d.depositAmountNeeded === true); // depdraft-3c8e5a14 — older estimates have no marker: absent = false
    return fp;
}

/** The fields that differ between two fingerprints ([] = no conflict). */
export function changedFields(before, after) {
    return CONFLICT_FIELDS.filter((f) => (before || {})[f] !== (after || {})[f]);
}

// Plain-English names for the message ("the line items and the status were changed").
const FIELD_WORDS = {
    customerId: 'the customer', customerName: 'the customer', customerDetails: 'the customer’s details',
    propertyId: 'the service location', propertyName: 'the service location', serviceAddress: 'the service location',
    // doced-8f3a61c2 — Stage 5e: the page's own labels ("Message to the customer", "Internal notes", "Terms")
    billTo: 'who is billed', title: 'the title', description: 'the message to the customer', notes: 'the internal notes', terms: 'the terms',
    docType: 'the document type', status: 'the status', createdAt: 'the date', expiresAt: 'the expiry date',
    estimatedDuration: 'the estimated duration', salesPersonId: 'the salesperson', lineItems: 'the line items',
    subtotal: 'the totals', taxAmount: 'the totals', total: 'the totals',
    deposit: 'the deposit', depositRequired: 'the deposit', depositStatus: 'the deposit', depositAmountNeeded: 'the deposit',
};
export function describeChanges(fields) {
    const words = [...new Set((fields || []).map((f) => FIELD_WORDS[f] || f))];
    if (!words.length) return '';
    return words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

export class EstimateConflictError extends Error {
    constructor(kind, fields = []) {
        const msg = kind === 'deleted' ? 'This estimate was deleted by someone else, so it was not saved.'
            : kind === 'exists' ? 'An estimate already exists with this id, so it was not saved.'
            : `Someone else changed this estimate while you were editing (${describeChanges(fields)}). Nothing was saved. Reload to see their changes, or save again to overwrite them.`;
        super(msg);
        this.name = 'EstimateConflictError';
        this.kind = kind; // 'changed' | 'deleted' | 'exists'
        this.fields = fields;
        // true = the page must offer Reload only (no "save again to overwrite").
        this.reloadOnly = kind !== 'changed' || fields.some((f) => RELOAD_ONLY_FIELDS.includes(f));
        if (kind === 'changed' && this.reloadOnly) {
            this.message = `Someone else changed this estimate while you were editing (${describeChanges(fields)}). Nothing was saved. Reload to see the change before editing again.`;
        }
    }
}

/**
 * Save with the check. Inside one transaction: read the estimate, compare with the fingerprint taken when
 * it was opened, and write only if nothing (in CONFLICT_FIELDS) changed. A NEW estimate must not exist yet.
 * overwrite: true lets a save go over OTHER people's edits — ONLY after the person has seen the conflict and
 * chosen to overwrite. It never goes over a status or deposit change (RELOAD_ONLY_FIELDS): those still refuse.
 *
 * { runTransaction, db, ref, write, isNew, baseline, overwrite } → resolves after the write;
 * throws EstimateConflictError (nothing written) on a conflict.
 */
export async function saveWithConflictCheck({ runTransaction, db, ref, write, isNew, baseline, overwrite = false }) {
    return runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (isNew) {
            if (snap.exists()) throw new EstimateConflictError('exists');
            tx.set(ref, write);
            return;
        }
        if (!snap.exists()) throw new EstimateConflictError('deleted');
        const fields = changedFields(baseline, fingerprintOf(snap.data()));
        if (fields.length && (!overwrite || fields.some((f) => RELOAD_ONLY_FIELDS.includes(f)))) {
            throw new EstimateConflictError('changed', fields);
        }
        tx.set(ref, write, { merge: true });
    });
}
